/**
 * Renders a raster image blob onto a canvas rotated clockwise by `rotationDeg`
 * (0, 90, 180, 270) and returns a JPEG blob suitable for download.
 */
export function rotateBlobWithCanvas(blob: Blob, rotationDeg: number): Promise<Blob> {
  const normalized = ((Math.round(rotationDeg) % 360) + 360) % 360;
  if (normalized === 0) {
    return Promise.resolve(blob);
  }

  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      try {
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          URL.revokeObjectURL(url);
          reject(new Error("Canvas 2D context unavailable"));
          return;
        }
        const rad = (normalized * Math.PI) / 180;
        if (normalized % 180 === 0) {
          canvas.width = w;
          canvas.height = h;
        } else {
          canvas.width = h;
          canvas.height = w;
        }
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate(rad);
        ctx.drawImage(img, -w / 2, -h / 2);
        canvas.toBlob(
          (b) => {
            URL.revokeObjectURL(url);
            if (b) resolve(b);
            else reject(new Error("canvas.toBlob failed"));
          },
          "image/jpeg",
          0.92
        );
      } catch (e) {
        URL.revokeObjectURL(url);
        reject(e);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load image for rotation"));
    };
    img.src = url;
  });
}
